<template>
    <div class="breadcrumb-wrapper" ref="breadcrumbWrapperRef">
        <primevue-breadcrumb :model="breadCrumbMenuItems">
            <template #item="{ item }">
                <prime-button
                    v-if="!isActiveItem(item.key)"
                    @click="item.onClick"
                    class="plain-btn"
                >
                    {{ item.label }}
                </prime-button>
                <div v-else class="breadcrumb-span">
                    <icofont-icon
                        v-if="item.icon"
                        :icon="item.icon"
                        :background-colour="item.colour"
                        size="extra-small"
                    />
                    {{ item.label }}
                </div>
            </template>
            <template #separator>
                <font-awesome-icon class="breadcrumb-separator" :icon="['fas', 'chevron-right']" />
            </template>
        </primevue-breadcrumb>
    </div>
</template>

<script lang="ts" setup>

import {
    computed, ref, watch, nextTick, onMounted,
} from 'vue';
import PrimevueBreadcrumb from 'primevue/breadcrumb';
import PrimeButton from 'primevue/button';
import { IBreadcrumbItem } from '@/interfaces/general/IBreadcrumbItem.ts';
import IcofontIcon from '@/components/powerMarkets/common/IcofontIcon.vue';

const props = defineProps<{
    breadcrumbItems: Array<IBreadcrumbItem>;
}>();

const MAX_LABEL_LENGTH = 30;

const breadcrumbWrapperRef = ref<HTMLElement | null>(null);

const breadCrumbMenuItems = computed(() => props.breadcrumbItems.map((item) => ({
    label: truncateLabel(item.label),
    onClick: item.onClick,
    key: item.id,
    icon: item.icofontIcon,
    colour: item.colour,
})));

function isActiveItem (itemKey: string | undefined): boolean {
    if (!itemKey) return false;
    return itemKey === props.breadcrumbItems[props.breadcrumbItems.length - 1].id;
}

function truncateLabel (label: string): string {
    if (label.length > MAX_LABEL_LENGTH) {
        return `${label.slice(0, MAX_LABEL_LENGTH - 1)}…`;
    }
    return label;
}

async function scrollToEnd (): Promise<void> {
    await nextTick();
    if (!breadcrumbWrapperRef.value) return;

    const wrapper = breadcrumbWrapperRef.value.querySelector('.p-breadcrumb') as HTMLElement | null;
    if (!wrapper) return;
    const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
    if (maxScroll <= 0) return;
    wrapper.scrollTo({ left: maxScroll, behavior: 'smooth' });
}

watch(() => props.breadcrumbItems.length, async (newLen, oldLen) => {
    if (newLen > (oldLen ?? 0)) {
        await scrollToEnd();
    }
});

onMounted(async () => {
    if (props.breadcrumbItems && props.breadcrumbItems.length) {
        await scrollToEnd();
    }
});

</script>

<style scoped>
.breadcrumb-wrapper {
    overflow: hidden;
    :deep(.p-breadcrumb-list) {
        flex-wrap: nowrap;
        white-space: nowrap;
    }
    .breadcrumb-span {
        display: flex;
        gap: var(--smallSpacing);
        align-items: center;

        .icon-wrapper {
            margin: var(--extraSmallSpacing) 0;
        }
    }
}
</style>
